import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { accessLocalStorage, listUsers, deleteUser } from '../../../services';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './UsersList.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faPlus, faEye, faUser } from '@fortawesome/free-solid-svg-icons';

function UsersList() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsChangeFormElements, toggleIsHidden, setItemsSelectedToEdit } = getItensStateGlobal;

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [filterText, setFilterText] = useState('');

  const history = useHistory();

  const getRoleDisplay = (role) => {
    if (role === 'root') return 'Técnico';
    if (role === 'admin') return 'Administrativo';
    return 'Usuário';
  };

  const getRoleBadgeClass = (role) => {
    if (role === 'root') return 'badge-role-root';
    if (role === 'admin') return 'badge-role-admin';
    return 'badge-role-user';
  };

  const getEndpointsviewerDisplay = (listEndpoint) => {
    if (listEndpoint === 'all') return 'Todos';
    if (listEndpoint === 'none') return 'Nenhum';
    if (Array.isArray(listEndpoint)) return listEndpoint.join(', ');
    if (typeof listEndpoint === 'string' && listEndpoint.includes(',')) {
      return listEndpoint.split(',').join(', ');
    }
    return listEndpoint || 'Nenhum';
  };

  const getQueuesviewerDisplay = (listQueues) => {
    if (listQueues === 'all') return 'Todas';
    if (listQueues === 'none') return 'Nenhuma';
    if (Array.isArray(listQueues)) return listQueues.join(', ');
    if (typeof listQueues === 'string' && listQueues.includes(',')) {
      return listQueues.split(',').join(', ');
    }
    return listQueues || 'Nenhuma';
  };

  const fetchUsers = async () => {
    try {
      const dataUser = await accessLocalStorage.getUserLocalStorage();
      if (!dataUser || !dataUser.user) {
        setLoading(false);
        return;
      }

      const { role, idtenant } = dataUser.user;
      let usersList = await listUsers();
      
      if (!usersList || !Array.isArray(usersList)) {
        setUsers([]);
        setLoading(false);
        return;
      }

      if (role === 'root' && idtenant === 'vgs') {
        usersList = usersList.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase().trim();
          const nameB = (b.name || '').toLowerCase().trim();
          return nameA.localeCompare(nameB, 'pt-BR');
        });
        setUsers(usersList);
      } else {
        usersList = usersList.filter((user) => {
          if (Array.isArray(idtenant)) {
            return idtenant.includes(user.idtenant);
          }
          return user.idtenant === idtenant;
        });
        usersList = usersList.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase().trim();
          const nameB = (b.name || '').toLowerCase().trim();
          return nameA.localeCompare(nameB, 'pt-BR');
        });
        setUsers(usersList);
      }

      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      setUsers([]);
      setLoading(false);
    }
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    
    const { user: { role } } = dataUser;
    if (role !== 'root') return history.push('/home');

    await fetchUsers();
  }, [history]);

  useEffect(() => {
    validateUserLogged();
  }, []);

  const handleEdit = (user) => {
    setItemsSelectedToEdit(user.email);
    toggleIsChangeFormElements();
    toggleIsHidden();
    history.push('/config/usuarios/editar');
  };

  const handleView = (user) => {
    setItemsSelectedToEdit(user);
    history.push('/config/usuarios/visualizar');
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowConfirmDelete(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    setShowConfirmDelete(false);
    const emailToDelete = userToDelete.email;
    setUserToDelete(null);

    try {
      await deleteUser({ email: emailToDelete });
      await fetchUsers();
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      await fetchUsers();
    }
  };

  const cancelDelete = () => {
    setShowConfirmDelete(false);
    setUserToDelete(null);
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'badge-active' : 'badge-inactive';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  const filteredUsers = users.filter((user) => {
    const searchText = filterText.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(searchText)) ||
      (user.email && user.email.toLowerCase().includes(searchText)) ||
      (user.endpoint && user.endpoint.toString().includes(searchText)) ||
      (user.role && getRoleDisplay(user.role).toLowerCase().includes(searchText)) ||
      (user.idtenant && user.idtenant.toLowerCase().includes(searchText))
    );
  });

  if (loading) return <Loading />;

  return (
    <div className="users-container">
      <div className="users-header">
        <div className="users-title-wrapper">
          <svg className="users-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <div>
            <h1 className="users-title">Usuários</h1>
            <p className="users-subtitle">Gerenciar usuários do sistema</p>
          </div>
        </div>
        <div className="users-actions">
          <Link to="/config/usuarios/novo" className="btn-new-user">
            <FontAwesomeIcon icon={faPlus} />
            Novo Usuário
          </Link>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-input-wrapper">
          <svg className="filter-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            className="filter-input"
            placeholder="Filtrar por nome, e-mail, ramal, tipo ou tenant..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          {filterText && (
            <button className="filter-clear" onClick={() => setFilterText('')}>
              ×
            </button>
          )}
        </div>
        <div className="filter-results">
          Exibindo {filteredUsers.length} de {users.length} usuário(s)
        </div>
      </div>

      {users.length === 0 ? (
        <div className="empty-state">
          <svg className="empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <div className="empty-state-title">Nenhum usuário encontrado</div>
          <p className="empty-state-text">
            Clique em "Novo Usuário" para criar seu primeiro usuário.
          </p>
        </div>
      ) : (
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead className="users-table-header">
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Ramal</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Tenant</th>
                <th>Visão Ramais</th>
                <th>Visão Filas</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.email}>
                  <td>
                    <strong>{user.name}</strong>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className="user-badge badge-endpoint">
                      {user.endpoint || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={`user-badge ${getRoleBadgeClass(user.role)}`}>
                      {getRoleDisplay(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`user-badge ${getStatusColor(user.active)}`}>
                      {getStatusText(user.active)}
                    </span>
                  </td>
                  <td>
                    <span className="user-badge badge-tenant">
                      {user.idtenant || 'N/A'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {getEndpointsviewerDisplay(user.endpointsviewer)}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {getQueuesviewerDisplay(user.queuesviewer)}
                  </td>
                  <td>
                    <div className="users-actions-cell">
                      <button
                        className="btn-user-action btn-view-user"
                        onClick={() => handleView(user)}
                        title="Visualizar"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        className="btn-user-action btn-edit-user"
                        onClick={() => handleEdit(user)}
                        title="Editar"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        className="btn-user-action btn-delete-user"
                        onClick={() => confirmDelete(user)}
                        title="Excluir"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      {showConfirmDelete && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-card">
            <header className="delete-modal-header">
              <p className="delete-modal-title">Confirmar Exclusão</p>
              <button 
                className="modal-close-btn" 
                aria-label="close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </header>
            <section className="delete-modal-body">
              <p>
                Tem certeza que deseja excluir o usuário{' '}
                <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
              </p>
              <div className="delete-modal-warning">
                <strong>Esta ação não pode ser desfeita!</strong>
              </div>
            </section>
            <footer className="delete-modal-footer">
              <button 
                className="btn-modal btn-modal-cancel"
                onClick={cancelDelete}
              >
                Cancelar
              </button>
              <button 
                className="btn-modal btn-modal-delete"
                onClick={handleDelete}
              >
                Sim, Excluir
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersList;
